import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import useToggleFavorite from "../hooks/useToggleFavorite";
import { auth, db as defaultDb } from "../firebase";

const FavoritesContext = createContext({
  favoritesSet: new Set(),
  favoritesArray: [],
  loading: true,
  toggleFavorite: async () => {},
});

export function FavoritesProvider({ children, db = defaultDb }) {
  const [uid, setUid] = useState(auth && auth.currentUser ? auth.currentUser.uid : null);
  const [favoritesArray, setFavoritesArray] = useState([]);
  const [favoritesSet, setFavoritesSet] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const toggleFavorite = useToggleFavorite(db);

  // auth listener to get uid
  useEffect(() => {
    let unsubAuth = null;
    try {
      unsubAuth = onAuthStateChanged(auth, (u) => {
        setUid(u ? u.uid : null);
      });
    } catch (e) {
      setUid(auth && auth.currentUser ? auth.currentUser.uid : null);
    }
    return () => {
      try { if (typeof unsubAuth === "function") unsubAuth(); } catch (_) {}
    };
  }, []);

  // subscribe to users/{uid}.favorites when uid exists
  useEffect(() => {
    if (!db || !uid) {
      setFavoritesArray([]);
      setFavoritesSet(new Set());
      setLoading(false);
      return;
    }
    setLoading(true);
    const uRef = doc(db, "users", uid);
    const unsub = onSnapshot(
      uRef,
      (snap) => {
        try {
          const data = snap.exists() ? snap.data() || {} : {};
          const favsRaw = Array.isArray(data.favorites) ? data.favorites : [];
          const favs = favsRaw.map((x) => (typeof x === "string" ? x : String(x)));
          setFavoritesArray(favs);
          setFavoritesSet(new Set(favs));
        } catch (e) {
          console.error("FavoritesProvider processing error", e);
          setFavoritesArray([]);
          setFavoritesSet(new Set());
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error("FavoritesProvider snapshot error", err);
        setFavoritesArray([]);
        setFavoritesSet(new Set());
        setLoading(false);
      }
    );
    return () => {
      try { unsub(); } catch (_) {}
    };
  }, [db, uid]);

  const value = useMemo(() => ({ favoritesSet, favoritesArray, loading, toggleFavorite }), [favoritesSet, favoritesArray, loading, toggleFavorite]);

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavoritesContext() {
  return useContext(FavoritesContext);
}